import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { toast } from "sonner";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

export interface ICheckUserParams {
	username: string;
	vendorId: number;
}

export interface ICheckUserResponseItem {
	username: string;
	vendorId: number;
}

interface ICheckUserState {
	loading: boolean;
	error: string | null;
	data: Array<ICheckUserResponseItem>;
}

const initialState: ICheckUserState = {
	loading: false,
	error: null,
	data: [],
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const buildUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/User/CheckExistingUsernames`;
};

export const checkUser = createAsyncThunk<Array<ICheckUserResponseItem>, ICheckUserParams>(
	"passwordManager/checkUser",
	async ({ username, vendorId }, { rejectWithValue, dispatch }) => {
		try {
			const body = {
				UsernamesAndVendorId: [
					{
						Username: username,
						VendorId: vendorId,
					},
				],
			};
			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.post<Array<ICheckUserResponseItem>>(buildUrl(), body, { headers: getAuthHeaders() }),
			);
			return res.data ?? [];
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to check user";
			toast.error(message);
			return rejectWithValue(message);
		}
	},
);

const checkUserSlice = createSlice({
	name: "passwordManager/checkUser",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(checkUser.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(checkUser.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.data = action.payload;
			})
			.addCase(checkUser.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to check user";
			});
	},
});

export const checkUserReducer = checkUserSlice.reducer;
